// /embed/picker — the body-area picker on its own, for WordPress pages to load in an
// iframe through loader.js (<div data-expy-onboarding="picker">).
//
// Why an iframe and not an Elementor section: expyhealth.com runs the Meta pixel and
// Hotjar, and both would record which body area a visitor tapped. Neither can see inside
// a cross-origin frame, so the tap stays on our origin.
//
// A tap sends the top window to the funnel with `?prefill=<area>` plus the utm_* / ref the
// loader forwarded; the funnel seeds question one from it, opens on question two and
// drops the token from the address bar (lib/prefill.ts). The area travels in the URL,
// not in storage: storage written inside this frame did not reach the full-page funnel on
// a phone (John, 2026-09-24), so the frame writes nothing.
import { useEffect } from 'react';
import { observeHeight } from '../lib/postMessage';
import { useIsNarrow } from '../lib/useIsNarrow';
import { AreaPicker } from './AreaPicker';

// Besides the tapped area (prefill), only attribution travels to the top window.
const FORWARD = ['ref', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function funnelUrl(area: string): string {
  const incoming = new URLSearchParams(window.location.search);
  const out = new URLSearchParams({ prefill: area.toLowerCase() });
  for (const key of FORWARD) {
    const value = incoming.get(key);
    if (value) out.set(key, value);
  }
  return `${window.location.origin}/?${out.toString()}`;
}

export function EmbedPicker() {
  const narrow = useIsNarrow();

  // Sit on the host page's background, and keep the frame sized to the card.
  useEffect(() => {
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return observeHeight();
  }, []);

  return (
    // Padding keeps the card's shadow from being clipped by the frame edge.
    <div style={{ padding: 12 }}>
      <AreaPicker
        narrow={narrow}
        onPick={(area) => {
          const url = funnelUrl(area);
          // A tap is a user gesture, so the frame may navigate the top window.
          try {
            window.top!.location.href = url;
          } catch {
            window.open(url, '_blank', 'noopener');
          }
        }}
      />
    </div>
  );
}
